import { computed, ref } from 'vue';

import { OrderEditType } from '@/constants/orderEditType';
import { ordersMixins } from '@/mixins/orders.mixins';
import { useOrderStore } from '@/stores/order';
import { useProductMatrixStore } from '@/stores/product-matrix';
import { useUserStore } from '@/stores/user';
import type { Draft } from '@/types/draft.interface';
import type { EditableOrder } from '@/types/order.interface';
import { OrderType } from '@onrunning/backstage-models';

export function useOrderValidation(editType?: OrderEditType) {
  const userStore = useUserStore();
  const orderStore = useOrderStore();
  const productMatrixStore = useProductMatrixStore();

  const isOrderDetailsValid = ref(true);
  const isTimeoutForOrderEditing = ref(false);
  const isNewDraft = computed(() => !editType);
  const isDraftEditMode = computed(() => editType === OrderEditType.Draft);
  const isOrderEditMode = computed(() => editType === OrderEditType.Order);
  const isMultipleOrder = computed(() => orderStore.orderType === OrderType.MultiplePreOrder);
  const isPreOrder = computed(() => orderStore.orderType === OrderType.PreOrder);
  const isPreOrderVariant = computed(() => ordersMixins.isPreOrderVariant(orderStore.orderType));

  const orders = computed(() => {
    return new Map([...orderStore.orders, ...orderStore.updatedOrders]);
  });

  const orderHasValidContactId = (order: EditableOrder | Draft): boolean => {
    if (!userStore.isASalesRep) return true;

    return !!order.contactId;
  };

  const isReceiptDateValid = (dateToValidate: string | Date): boolean => {
    const endDateValue = orderStore.orderTypeInfo?.endDate;

    if (!endDateValue) {
      return true;
    }

    const endDate = new Date(endDateValue);

    return !isNaN(endDate.getTime()) ? new Date(dateToValidate) <= endDate : true;
  };

  const orderStats = computed(() => {
    let isRequestedReceiptDateValid = true;
    let hasValidContactId = true;

    for (const order of orders.value.values()) {
      if (isPreOrderVariant.value) {
        isRequestedReceiptDateValid = isRequestedReceiptDateValid && isReceiptDateValid(order.dueDate);
      }
      hasValidContactId = orderHasValidContactId(order as EditableOrder | Draft);
    }

    return { hasValidContactId, isRequestedReceiptDateValid };
  });

  const matrixStats = computed(() => {
    let overallTotal = 0;
    let allQuantitiesValid = true;
    let isRequestedReceiptDateValid = true;

    for (const matrixOrder of productMatrixStore.matrixOrders) {
      overallTotal += matrixOrder.total;
      allQuantitiesValid = allQuantitiesValid && matrixOrder.total > 0;

      if (isPreOrderVariant.value) {
        isRequestedReceiptDateValid = isRequestedReceiptDateValid && isReceiptDateValid(matrixOrder.date);
      }
    }

    return { overallTotal, allQuantitiesValid, isRequestedReceiptDateValid };
  });

  const canDealerEditOrder = computed(() => {
    return (
      userStore.isADealer &&
      !isTimeoutForOrderEditing.value &&
      ordersMixins.isOrderEditableForDealer(orderStore.activeOrder as EditableOrder, userStore.userProfile?.user)
    );
  });

  const isDealerInReadOnlyMode = computed(() => {
    return isOrderEditMode.value && userStore.isADealer && !canDealerEditOrder.value;
  });

  const isOrderEdited = computed(() => {
    return !isDealerInReadOnlyMode.value && (productMatrixStore.matrixUpdated || orderStore.orderUpdated);
  });

  const isSubmitEnabled = computed(() => {
    return isOrderEditMode.value ? isOrderEdited.value && isOrderValidForSubmit.value : isOrderValidForSubmit.value;
  });

  const isOrderValidForSubmit = computed(() => {
    return (
      isSaveDraftEnabled.value &&
      isOrderDetailsValid.value &&
      orderStats.value.isRequestedReceiptDateValid &&
      orderStats.value.hasValidContactId
    );
  });

  const isSaveDraftEnabled = computed(() => {
    return matrixStats.value.allQuantitiesValid && matrixStats.value.isRequestedReceiptDateValid;
  });

  const isPreOrderAndOutsideInitialSellIn = computed((): boolean => {
    if (!orderStore.activeOrder) {
      return false;
    }

    if (!isPreOrderVariant.value) {
      return false;
    }

    const preOrderSeasonName = orderStore.orderTypeInfo?.preOrderSeasonName;
    const activeOrderSeasons = orderStore.activeOrder?.seasons;

    if (!preOrderSeasonName) {
      return true;
    }

    const isSeasonIncluded = activeOrderSeasons?.includes(preOrderSeasonName) ?? false;

    return !isSeasonIncluded;
  });

  return {
    orderStats,
    matrixStats,
    isSubmitEnabled,
    isOrderDetailsValid,
    isSaveDraftEnabled,
    isTimeoutForOrderEditing,
    isPreOrderAndOutsideInitialSellIn,
    canDealerEditOrder,
    isDraftEditMode,
    isOrderEditMode,
    isNewDraft,
    isMultipleOrder,
    isPreOrder,
    isPreOrderVariant,
    isDealerInReadOnlyMode,
  };
}
